import React from 'react';
import { Skeleton } from '@mui/material';
import { CalendarMonth } from '@mui/icons-material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white shadow-lg rounded-xl border border-gray-100 p-3 text-xs">
      <p className="font-semibold text-gray-700 mb-1">DOC {label} days</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
      <p className="text-gray-500 mt-1">
        Total: <strong>{payload.reduce((s, p) => s + p.value, 0)}</strong>
      </p>
    </div>
  );
};

const PolicyAgingSection = ({ data = [], loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
        <Skeleton variant="text" width={200} height={28} />
        <Skeleton variant="rounded" height={260} sx={{ mt: 2 }} />
      </div>
    );
  }

  const totalActive = data.reduce((s, d) => s + d.active, 0);
  const totalClaimed = data.reduce((s, d) => s + d.claimed, 0);
  const totalExpired = data.reduce((s, d) => s + d.expired, 0);

  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
      <div className="flex items-center gap-2 mb-4">
        <CalendarMonth sx={{ fontSize: 18, color: '#6366f1' }} />
        <h3 className="text-sm font-semibold text-gray-700">Policy Aging (DOC-wise)</h3>
        <div className="ml-auto flex items-center gap-3 text-xs text-gray-500">
          <span>
            Active: <strong className="text-blue-600">{totalActive}</strong>
          </span>
          <span>
            Claimed: <strong className="text-amber-600">{totalClaimed}</strong>
          </span>
          <span>
            Expired: <strong className="text-gray-600">{totalExpired}</strong>
          </span>
        </div>
      </div>

      {data.every((d) => d.active + d.claimed + d.expired === 0) ? (
        <div className="flex items-center justify-center py-14 text-sm text-gray-400">
          No policy aging data available
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis
              dataKey="bucket"
              tick={{ fontSize: 11, fill: '#94a3b8' }}
              axisLine={{ stroke: '#e2e8f0' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#94a3b8' }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
            />
            <Bar
              dataKey="active"
              name="Active"
              stackId="a"
              fill="#3b82f6"
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="claimed"
              name="Claimed"
              stackId="a"
              fill="#f59e0b"
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="expired"
              name="Expired"
              stackId="a"
              fill="#94a3b8"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default PolicyAgingSection;
